import React, { useEffect, useState } from "react";
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Swal from "sweetalert2";
import { ProjectList, getProjectExcludeMappings, updateProjectExcludeMappings } from "../../../Api/Project";
import { deleteAPI } from "../../../ApiService";

function TabGroupChildExclude(props) {
    const projectDetailID = props?.detail?.childProjects?.[0]?.projectDetailID;
    const [searchOptions, setSearchOptions] = useState([]);
    const [mappings, setMappings] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);

    useEffect(() => {
        if (projectDetailID) {
            loadMappings();
        }
    }, [projectDetailID]);

    const loadMappings = async () => {
        let res = await getProjectExcludeMappings({ projectDetailID });
        if (res.status === 1) {
            setMappings(res.payload.map(row => ({
                excludedProjectDetailID: row.excludedProjectDetailID,
                projectCode: row.projectCode,
                fullName: row.fullName,
                complete: row.complete === 1,
                terminate: row.terminate === 1,
                overQuota: row.overQuota === 1,
                dropOut: row.dropOut === 1,
            })));
        } else {
            setMappings([]);
        }
    };

    const searchProjects = async (keyword, reason) => {
        if (reason === 'reset') {
            setSearchOptions([]);
            return;
        }
        if (!keyword || keyword.trim().length < 2) {
            setSearchOptions([]);
            return;
        }
        setSearchLoading(true);
        let res = await ProjectList({ page: 1, recPerPage: 20, searchKeyword: keyword.trim() });
        if (res.status === 1) {
            let options = [];
            res.payload?.forEach((row) => {
                if (row.hasChild && row.childProjects?.length > 0) {
                    row.childProjects.forEach((child) => {
                        if (child.projectDetailID !== projectDetailID) {
                            options.push({
                                projectDetailID: child.projectDetailID,
                                projectCode: child.projectCode,
                                fullName: child.fullName,
                                projectDetailFullName: `${child.projectCode} : ${child.fullName}`,
                            });
                        }
                    });
                } else if (row.projectDetailID && row.projectDetailID !== projectDetailID) {
                    options.push({
                        projectDetailID: row.projectDetailID,
                        projectCode: row.projectCode,
                        fullName: row.fullName,
                        projectDetailFullName: `${row.projectCode} : ${row.fullName}`,
                    });
                }
            });
            setSearchOptions(options);
        } else {
            setSearchOptions([]);
        }
        setSearchLoading(false);
    };

    const handleProjectSelect = (project) => {
        if (!project?.projectDetailID) return;
        const exists = mappings.some(m => m.excludedProjectDetailID === project.projectDetailID);
        if (exists) {
            setSelectedProject(null);
            return;
        }

        setMappings(prev => [...prev, {
            excludedProjectDetailID: project.projectDetailID,
            projectCode: project.projectCode,
            fullName: project.fullName,
            complete: false,
            terminate: false,
            overQuota: false,
            dropOut: false,
        }]);
        setSelectedProject(null);
        setSearchOptions([]);
    };

    const updateMappingField = (index, field, value) => {
        setMappings(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            if (field !== 'selectAll') {
                const row = updated[index];
                row.selectAll = row.complete && row.terminate && row.overQuota && row.dropOut;
            } else {
                updated[index].complete = value;
                updated[index].terminate = value;
                updated[index].overQuota = value;
                updated[index].dropOut = value;
            }
            return updated;
        });
    };

    const handleSelectAll = (index, checked) => {
        updateMappingField(index, 'selectAll', checked);
    };

    const handleDelete = async (excludedProjectDetailID) => {
        const result = await Swal.fire({
            text: 'Are you sure you want to delete?',
            confirmButtonText: 'Yes',
            showCancelButton: true,
        });
        if (!result.value) return;

        let res = await deleteAPI('/project/exclude/' + projectDetailID + '/' + excludedProjectDetailID);
        setMappings(prev => prev.filter(m => m.excludedProjectDetailID !== excludedProjectDetailID));
        if (res?.status === 1) {
            Swal.fire({ text: res.message, timer: 1500 });
        }
    };

    const handleUpdate = async () => {
        const payload = {
            projectDetailID,
            mappings: mappings.map(m => ({
                excludedProjectDetailID: m.excludedProjectDetailID,
                complete: m.complete ? 1 : 0,
                terminate: m.terminate ? 1 : 0,
                overQuota: m.overQuota ? 1 : 0,
                dropOut: m.dropOut ? 1 : 0,
            }))
        };
        let res = await updateProjectExcludeMappings(payload);
        if (res?.status === 1) {
            loadMappings();
        }
    };

    return (
        <div className="card-body">
            <div className="row">
                <div className="col-md-6 col-lg-6">
                    <div className="mb-3">
                        <label className="form-label mb-0">Search Project <span className="required_field">*</span></label>
                        <Autocomplete
                            id="excludeProjectSearch"
                            freeSolo
                            size="small"
                            value={selectedProject}
                            onInputChange={(e, value, reason) => searchProjects(value, reason)}
                            options={searchOptions}
                            onChange={(e, value) => handleProjectSelect(value)}
                            getOptionLabel={(option) => typeof option === 'string' ? option : (option?.projectDetailFullName || '')}
                            filterOptions={(x) => x}
                            loading={searchLoading}
                            loadingText="Searching..."
                            noOptionsText="No projects found"
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Search"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {searchLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </div>
                </div>
            </div>
            <div className="table-responsive">
                <table className="table table-bordered table-striped align-middle">
                    <thead className="table-light">
                        <tr>
                            <th scope="col">S.No</th>
                            <th scope="col">Project</th>
                            <th scope="col">Select All</th>
                            <th scope="col">Complete</th>
                            <th scope="col">Terminate</th>
                            <th scope="col">OverQuota</th>
                            <th scope="col">DropOut</th>
                            <th scope="col">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {mappings.length === 0 ? (
                            <tr>
                                <td colSpan="8" className="text-center">No excluded projects mapped</td>
                            </tr>
                        ) : mappings.map((row, index) => (
                            <tr key={row.excludedProjectDetailID}>
                                <td>{index + 1}</td>
                                <td>{row.projectCode} : {row.fullName}</td>
                                <td>
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={row.complete && row.terminate && row.overQuota && row.dropOut}
                                        onChange={(e) => handleSelectAll(index, e.target.checked)}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={row.complete}
                                        onChange={(e) => updateMappingField(index, 'complete', e.target.checked)}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={row.terminate}
                                        onChange={(e) => updateMappingField(index, 'terminate', e.target.checked)}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={row.overQuota}
                                        onChange={(e) => updateMappingField(index, 'overQuota', e.target.checked)}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={row.dropOut}
                                        onChange={(e) => updateMappingField(index, 'dropOut', e.target.checked)}
                                    />
                                </td>
                                <td>
                                    <span className="p-1">
                                        <i
                                            className="ri-delete-bin-2-line del pointerC"
                                            onClick={() => handleDelete(row.excludedProjectDetailID)}
                                        ></i>
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {mappings.length > 0 && mappings.some(m => m.complete || m.terminate || m.overQuota || m.dropOut) && (
                <div className="text-end">
                    <button type="button" className="btn btn-primary" onClick={handleUpdate}>Update</button>
                </div>
            )}
        </div>
    );
}

export default TabGroupChildExclude;
